import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  Rocket, ArrowRight, ArrowLeft, Car, Globe, CheckCircle2,
  MapPin, Phone, Clock, Sparkles, Loader2, ExternalLink, Star,
  Building2, FileText, Users, Upload, X, UserPlus, Eye, EyeOff
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_BASE = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'identity', title: 'Identité', icon: Building2 },
  { id: 'info', title: 'Coordonnées', icon: MapPin },
  { id: 'taxes', title: 'Taxes & Permis', icon: FileText },
  { id: 'website', title: 'Site web', icon: Globe },
  { id: 'vehicle', title: 'Véhicule', icon: Car },
  { id: 'team', title: 'Équipe', icon: Users },
];

const TEMPLATES = [
  { key: 'classic', name: 'Classique', color: '#2563eb', img: '/assets/preview-classic.jpg' },
  { key: 'modern_dark', name: 'Moderne Sombre', color: '#dc2626', img: '/assets/preview-modern-dark.jpg' },
  { key: 'elegant', name: 'Élégant Premium', color: '#047857', img: '/assets/preview-elegant.jpg' },
];

const OnboardingWizard = ({ onComplete }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [generatingSeo, setGeneratingSeo] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const dealerId = user?.dealer_id;
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  React.useEffect(() => {
    if (!dealerId) return;
    axios.get(`${API}/platform/dealers/${dealerId}`, { headers })
      .then(res => { if (res.data?.subdomain) setSubdomain(res.data.subdomain); })
      .catch(() => {});
  }, [dealerId]);

  // Step 1: Identity
  const [identity, setIdentity] = useState({ legal_name: '', dba_name: '', tagline: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Step 2: Contact
  const [info, setInfo] = useState({
    address: '', city: '', province: 'QC', postal_code: '', phone: '',
    hours: {
      lundi: '9:00 - 18:00', mardi: '9:00 - 18:00', mercredi: '9:00 - 18:00',
      jeudi: '9:00 - 18:00', vendredi: '9:00 - 18:00', samedi: '9:00 - 17:00', dimanche: 'Ferme',
    },
  });

  // Step 3: Taxes
  const [taxes, setTaxes] = useState({ tps_rate: '5.0', tvq_rate: '9.975', saaq_license: '', saaq_file: '' });

  // Step 4: Website
  const [template, setTemplate] = useState('classic');
  const [primaryColor, setPrimaryColor] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDesc, setSeoDesc] = useState('');

  // Step 5: Vehicle
  const [vehicle, setVehicle] = useState({ year: '', make: '', model: '', price: '', mileage: '', stock_number: '' });
  const [vehicleAdded, setVehicleAdded] = useState(false);

  // Step 6: Team
  const [teamMember, setTeamMember] = useState({ name: '', email: '', role: 'user' });
  const [memberInvited, setMemberInvited] = useState(false);

  // Preview
  const [showPreview, setShowPreview] = useState(false);

  const markStep = async (stepId) => {
    if (!dealerId) return;
    try { await axios.post(`${API}/signup/onboarding/${dealerId}/complete-step?step_id=${stepId}`); } catch {}
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  // Save Step 1: Identity + Logo
  const saveIdentity = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (identity.legal_name) updates.legal_name = identity.legal_name;
      if (identity.dba_name) updates.dba_name = identity.dba_name;
      if (identity.tagline) updates.tagline = identity.tagline;
      if (Object.keys(updates).length > 0) {
        await axios.put(`${API}/dealer-settings/${dealerId}`, updates, { headers });
      }
      if (logoFile) {
        setUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', logoFile);
        formData.append('asset_type', 'logo');
        await axios.post(`${API}/website/upload/branding/${dealerId}`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        setUploadingLogo(false);
      }
      await markStep('profile');
      toast.success('Identité sauvegardée!');
      setStep(2);
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); setUploadingLogo(false); }
  };

  // Save Step 2: Contact + Hours
  const saveInfo = async () => {
    if (!info.phone) { toast.error('Le telephone est requis'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/dealer-settings/${dealerId}`, {
        address: { street: info.address || '', city: info.city || '', state: info.province || 'QC', zip_code: info.postal_code || '', country: 'Canada' },
        contact: { phone: info.phone, email: user?.email || '' },
      }, { headers });
      await axios.put(`${API}/website/business-hours/${dealerId}`, info.hours, { headers });
      toast.success('Coordonnées sauvegardées!');
      setStep(3);
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  // Save Step 3: Taxes
  const saveTaxes = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/dealer-settings/${dealerId}`, {
        tps_rate: parseFloat(taxes.tps_rate) || 5.0,
        tvq_rate: parseFloat(taxes.tvq_rate) || 9.975,
        saaq_license_number: taxes.saaq_license || '',
        saaq_file_number: taxes.saaq_file || '',
      }, { headers });
      await markStep('taxes');
      toast.success('Taxes et permis sauvegardés!');
      setStep(4);
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  // Save Step 4: Website
  const saveWebsite = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/website/settings/${dealerId}`, {
        template, primary_color: primaryColor || undefined,
        seo_title: seoTitle || undefined, seo_description: seoDesc || undefined,
        website_enabled: true,
      }, { headers });
      await markStep('website');
      toast.success('Site web configuré!');
      setStep(5);
    } catch { toast.error('Erreur de sauvegarde'); }
    finally { setSaving(false); }
  };

  // AI SEO
  const generateSeo = async () => {
    setGeneratingSeo(true);
    try {
      const res = await axios.post(`${API}/website/generate-seo/${dealerId}`, {}, { headers });
      if (res.data.seo_title) setSeoTitle(res.data.seo_title);
      if (res.data.seo_description) setSeoDesc(res.data.seo_description);
      toast.success('SEO généré par IA!');
    } catch { toast.error('Erreur de generation'); }
    finally { setGeneratingSeo(false); }
  };

  // Save Step 5: Vehicle
  const addVehicle = async () => {
    if (!vehicle.year || !vehicle.make || !vehicle.model) { toast.error('Annee, marque et modele requis'); return; }
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await axios.post(`${API}/inventory/vehicles`, {
        date: today, annee: parseInt(vehicle.year), marque: vehicle.make, modele: vehicle.model,
        stock: vehicle.stock_number || `STK${Date.now().toString().slice(-4)}`,
        niv: 'A COMPLETER', km: parseInt(vehicle.mileage) || 0, couleur: 'N/A',
        transmission: 'Automatique', prix_detail: parseFloat(vehicle.price) || 0,
        disponible: 'Oui', type: 'Detail', emplacement: 'IDS',
      }, { headers });
      setVehicleAdded(true);
      await markStep('inventory');
      toast.success('Véhicule ajouté!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  // Save Step 6: Invite Team
  const inviteTeamMember = async () => {
    if (!teamMember.email || !teamMember.name) { toast.error('Nom et courriel requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/users`, {
        email: teamMember.email, name: teamMember.name, role: teamMember.role,
        password: `Temp${Date.now().toString().slice(-6)}!`, dealer_id: dealerId,
      }, { headers });
      setMemberInvited(true);
      await markStep('team');
      toast.success(`${teamMember.name} ajouté à l'équipe!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const finish = async () => {
    try { await axios.put(`${API}/signup/dealers/${dealerId}/onboarding-complete`); } catch {}
    onComplete();
  };

  const totalSteps = STEPS.length + 2; // welcome + done
  const progress = step === 0 ? 0 : (step / totalSteps) * 100;
  const siteUrl = `${SITE_BASE}/site/${subdomain}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100 flex-shrink-0">
          <div className="h-full transition-all duration-500 rounded-r-full" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #2BAEF5, #FF8A3B)' }} />
        </div>

        {step > 0 && step < 7 && (
          <div className="flex items-center justify-center gap-2 px-4 pt-3 flex-shrink-0 overflow-x-auto">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-1.5 flex-shrink-0">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                  i + 1 < step ? 'bg-[#FF8A3B] text-white' : i + 1 === step ? 'bg-[#2BAEF5] text-white' : 'bg-gray-100 text-gray-400'
                }`}>{i + 1 < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}</div>
                <span className={`text-[10px] font-medium hidden lg:inline ${i + 1 === step ? 'text-gray-900' : 'text-gray-400'}`}>{s.title}</span>
                {i < STEPS.length - 1 && <div className="w-4 h-px bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-7 py-5">

          {/* ===== WELCOME ===== */}
          {step === 0 && (
            <div className="text-center" data-testid="step-welcome">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2BAEF5] to-[#1e3a8a] flex items-center justify-center mx-auto mb-5">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Configurez votre concessionnaire</h2>
              <p className="text-gray-500 text-sm mb-6">6 étapes pour être 100% opérationnel. Environ 5 minutes.</p>
              <div className="space-y-2.5 text-left mb-8 bg-gray-50 rounded-xl p-5">
                {STEPS.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-lg bg-[#2BAEF5]/10 flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-3.5 h-3.5 text-[#2BAEF5]" />
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{s.title}</span>
                    <span className="text-[10px] text-gray-400">{i + 1}/6</span>
                  </div>
                ))}
              </div>
              <Button onClick={() => setStep(1)} className="w-full py-5 text-base gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="start-setup-btn">
                C'est parti! <ArrowRight className="w-4 h-4" />
              </Button>
              <button onClick={finish} className="text-sm text-gray-400 mt-3 hover:text-gray-600 block mx-auto" data-testid="skip-all-btn">
                Configurer plus tard
              </button>
            </div>
          )}

          {/* ===== STEP 1: IDENTITY ===== */}
          {step === 1 && (
            <div data-testid="step-identity">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Identité de l'entreprise</h2>
              <p className="text-gray-500 text-sm mb-5">Ces informations apparaitront sur vos documents et votre site web.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Nom legal</Label>
                  <Input value={identity.legal_name} onChange={e => setIdentity({...identity, legal_name: e.target.value})}
                    placeholder="9876543 Quebec Inc." className="mt-1 bg-gray-50" data-testid="identity-legal" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Nom commercial (DBA)</Label>
                  <Input value={identity.dba_name} onChange={e => setIdentity({...identity, dba_name: e.target.value})}
                    placeholder="Auto Excellence Montreal" className="mt-1 bg-gray-50" data-testid="identity-dba" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Slogan / Tagline</Label>
                  <Input value={identity.tagline} onChange={e => setIdentity({...identity, tagline: e.target.value})}
                    placeholder="Votre satisfaction, notre priorite" className="mt-1 bg-gray-50" data-testid="identity-tagline" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium mb-2 block">Logo de l'entreprise</Label>
                  {logoPreview ? (
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <img src={logoPreview} alt="Logo" className="h-14 w-auto rounded-lg" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-600 font-medium">{logoFile?.name}</p>
                        <p className="text-[10px] text-gray-400">{(logoFile?.size / 1024).toFixed(0)} Ko</p>
                      </div>
                      <button onClick={() => { setLogoFile(null); setLogoPreview(null); }} className="text-gray-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 border-2 border-dashed border-gray-200 rounded-xl p-4 cursor-pointer hover:border-[#2BAEF5]/50 hover:bg-blue-50/30 transition-colors">
                      <Upload className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-gray-500">Cliquez pour televerser votre logo</span>
                      <span className="text-[10px] text-gray-400">PNG, JPG, WebP (max 5 Mo)</span>
                      <input type="file" accept="image/*" onChange={handleLogoSelect} className="hidden" data-testid="logo-upload-input" />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(0)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                <Button onClick={saveIdentity} disabled={saving || uploadingLogo} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="save-identity-btn">
                  {uploadingLogo ? 'Telechargement...' : saving ? 'Sauvegarde...' : 'Continuer'} <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 2: CONTACT ===== */}
          {step === 2 && (
            <div data-testid="step-info">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Coordonnées et horaires</h2>
              <p className="text-gray-500 text-sm mb-5">Vos clients pourront vous trouver facilement.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Téléphone *</Label>
                  <Input value={info.phone} onChange={e => setInfo({...info, phone: e.target.value})}
                    placeholder="(514) 555-1234" className="mt-1 bg-gray-50" data-testid="info-phone" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Adresse</Label>
                  <Input value={info.address} onChange={e => setInfo({...info, address: e.target.value})}
                    placeholder="123 boul. Exemple" className="mt-1 bg-gray-50" data-testid="info-address" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">Ville</Label>
                    <Input value={info.city} onChange={e => setInfo({...info, city: e.target.value})}
                      placeholder="Montreal" className="mt-1 bg-gray-50" data-testid="info-city" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">Province</Label>
                    <Input value={info.province} onChange={e => setInfo({...info, province: e.target.value})} placeholder="QC" className="mt-1 bg-gray-50" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">Code postal</Label>
                    <Input value={info.postal_code} onChange={e => setInfo({...info, postal_code: e.target.value})} placeholder="H2X 3L1" className="mt-1 bg-gray-50" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium mb-1.5 block"><Clock className="w-3.5 h-3.5 inline mr-1" />Horaires</Label>
                  <div className="space-y-1.5 bg-gray-50 rounded-lg p-3">
                    {[{key:'lundi',l:'Lun'},{key:'mardi',l:'Mar'},{key:'mercredi',l:'Mer'},{key:'jeudi',l:'Jeu'},{key:'vendredi',l:'Ven'},{key:'samedi',l:'Sam'},{key:'dimanche',l:'Dim'}].map(d => (
                      <div key={d.key} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-8 font-medium">{d.l}</span>
                        <Input value={info.hours[d.key]} className="bg-white h-7 text-xs"
                          onChange={e => setInfo({...info, hours: {...info.hours, [d.key]: e.target.value}})} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                <Button onClick={saveInfo} disabled={saving} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="save-info-btn">
                  {saving ? 'Sauvegarde...' : 'Continuer'} <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 3: TAXES & PERMIS ===== */}
          {step === 3 && (
            <div data-testid="step-taxes">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Taxes et permis</h2>
              <p className="text-gray-500 text-sm mb-5">Nécessaire pour vos factures et contrats de vente.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">Taux TPS (%)</Label>
                    <Input value={taxes.tps_rate} onChange={e => setTaxes({...taxes, tps_rate: e.target.value})}
                      placeholder="5.0" className="mt-1 bg-gray-50" data-testid="tax-tps" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 font-medium">Taux TVQ (%)</Label>
                    <Input value={taxes.tvq_rate} onChange={e => setTaxes({...taxes, tvq_rate: e.target.value})}
                      placeholder="9.975" className="mt-1 bg-gray-50" data-testid="tax-tvq" />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-xs text-blue-700">Les taux TPS (5%) et TVQ (9.975%) du Québec sont pré-remplis. Modifiez si nécessaire.</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Numéro de permis</Label>
                  <Input value={taxes.saaq_license} onChange={e => setTaxes({...taxes, saaq_license: e.target.value})}
                    placeholder="Ex: 123456" className="mt-1 bg-gray-50" data-testid="tax-saaq-license" />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 font-medium">Numéro de dossier</Label>
                  <Input value={taxes.saaq_file} onChange={e => setTaxes({...taxes, saaq_file: e.target.value})}
                    placeholder="Ex: 789012" className="mt-1 bg-gray-50" data-testid="tax-saaq-file" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setStep(2)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                <Button onClick={saveTaxes} disabled={saving} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="save-taxes-btn">
                  {saving ? 'Sauvegarde...' : 'Continuer'} <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 4: WEBSITE ===== */}
          {step === 4 && (
            <div data-testid="step-website">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Votre site web</h2>
              <p className="text-gray-500 text-sm mb-4">Choisissez votre design. Votre site sera en ligne instantanement!</p>
              <Label className="text-xs text-gray-600 font-medium mb-2 block">Choisissez un design</Label>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {TEMPLATES.map(tmpl => (
                  <button key={tmpl.key} onClick={() => { setTemplate(tmpl.key); setShowPreview(false); }}
                    className={`rounded-xl overflow-hidden border-2 transition-all ${template === tmpl.key ? 'border-[#2BAEF5] shadow-lg scale-[1.02]' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid={`select-template-${tmpl.key}`}>
                    <img src={tmpl.img} alt={tmpl.name} className="w-full h-20 object-cover object-top" />
                    <div className="p-2 text-center bg-white">
                      <span className="text-[11px] font-bold text-gray-800">{tmpl.name}</span>
                      <div className="w-2.5 h-2.5 rounded-full mx-auto mt-1" style={{ backgroundColor: tmpl.color }} />
                    </div>
                  </button>
                ))}
              </div>

              {/* Live Preview */}
              {subdomain && (
                <div className="mb-4">
                  <button onClick={() => setShowPreview(!showPreview)}
                    className="flex items-center gap-1.5 text-[11px] font-bold text-[#2BAEF5] hover:text-[#1a8ed4] mb-2 transition-colors"
                    data-testid="toggle-preview-btn">
                    {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    {showPreview ? 'Masquer l\'apercu' : 'Apercu en direct'}
                  </button>
                  {showPreview && (
                    <div className="rounded-xl border border-gray-200 overflow-hidden bg-gray-50 shadow-inner" data-testid="live-preview-container">
                      <div className="bg-gray-100 px-3 py-1.5 flex items-center gap-2 border-b border-gray-200">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-400" />
                          <div className="w-2 h-2 rounded-full bg-yellow-400" />
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                        </div>
                        <span className="text-[10px] text-gray-400 font-mono flex-1 text-center truncate">{subdomain}.autohub360.ca</span>
                      </div>
                      <iframe
                        key={`${template}-preview`}
                        src={`${SITE_BASE}/site/${subdomain}?preview_template=${template}`}
                        className="w-full border-0"
                        style={{ height: '220px', pointerEvents: 'none' }}
                        title="Apercu du site"
                        data-testid="site-preview-iframe"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <Label className="text-xs text-gray-600 font-medium mb-1.5 block">Couleur de marque (optionnel)</Label>
                <div className="flex items-center gap-3">
                  <input type="color" value={primaryColor || TEMPLATES.find(t => t.key === template)?.color || '#2563eb'}
                    onChange={e => setPrimaryColor(e.target.value)} className="w-9 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5" data-testid="onboard-color" />
                  <Input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} placeholder="Couleur par defaut du theme" className="bg-gray-50 text-sm flex-1" />
                </div>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-gray-600 font-medium">SEO (referencement Google)</Label>
                  <button onClick={generateSeo} disabled={generatingSeo} className="text-[11px] font-bold text-[#2BAEF5] hover:text-[#1a8ed4] flex items-center gap-1" data-testid="onboard-ai-seo">
                    {generatingSeo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
                    {generatingSeo ? 'Génération...' : 'Générer par IA'}
                  </button>
                </div>
                <Input value={seoTitle} onChange={e => setSeoTitle(e.target.value)} placeholder="Titre SEO" className="bg-gray-50 text-sm mb-2" data-testid="onboard-seo-title" />
                <textarea value={seoDesc} onChange={e => setSeoDesc(e.target.value)} placeholder="Description SEO"
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" rows={2} data-testid="onboard-seo-desc" />
              </div>
              <div className="flex gap-3 mt-4">
                <Button variant="outline" onClick={() => setStep(3)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                <Button onClick={saveWebsite} disabled={saving} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="save-website-btn">
                  {saving ? 'Sauvegarde...' : 'Mettre en ligne!'} <Sparkles className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ===== STEP 5: VEHICLE ===== */}
          {step === 5 && (
            <div data-testid="step-vehicle">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Premier véhicule</h2>
              <p className="text-gray-500 text-sm mb-5">Ajoutez un véhicule — il apparaît instantanément sur votre site.</p>
              {!vehicleAdded ? (
                <>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs text-gray-600 font-medium">Annee *</Label>
                        <Input value={vehicle.year} onChange={e => setVehicle({...vehicle, year: e.target.value})} placeholder="2022" className="mt-1 bg-gray-50" data-testid="vehicle-year" /></div>
                      <div><Label className="text-xs text-gray-600 font-medium">Marque *</Label>
                        <Input value={vehicle.make} onChange={e => setVehicle({...vehicle, make: e.target.value})} placeholder="Toyota" className="mt-1 bg-gray-50" data-testid="vehicle-make" /></div>
                      <div><Label className="text-xs text-gray-600 font-medium">Modele *</Label>
                        <Input value={vehicle.model} onChange={e => setVehicle({...vehicle, model: e.target.value})} placeholder="Corolla" className="mt-1 bg-gray-50" data-testid="vehicle-model" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-xs text-gray-600 font-medium">Prix ($)</Label>
                        <Input value={vehicle.price} onChange={e => setVehicle({...vehicle, price: e.target.value})} placeholder="18500" className="mt-1 bg-gray-50" data-testid="vehicle-price" /></div>
                      <div><Label className="text-xs text-gray-600 font-medium">Kilometrage</Label>
                        <Input value={vehicle.mileage} onChange={e => setVehicle({...vehicle, mileage: e.target.value})} placeholder="45000" className="mt-1 bg-gray-50" data-testid="vehicle-mileage" /></div>
                      <div><Label className="text-xs text-gray-600 font-medium">No. Stock</Label>
                        <Input value={vehicle.stock_number} onChange={e => setVehicle({...vehicle, stock_number: e.target.value})} placeholder="STK001" className="mt-1 bg-gray-50" data-testid="vehicle-stock" /></div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setStep(4)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                    <Button variant="ghost" onClick={() => setStep(6)} className="text-gray-400">Passer</Button>
                    <Button onClick={addVehicle} disabled={saving} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="add-vehicle-btn">
                      {saving ? 'Ajout...' : 'Ajouter'} <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-base font-bold text-gray-900">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                  <p className="text-sm text-gray-500 mb-5">Visible sur votre site!</p>
                  <Button onClick={() => setStep(6)} className="w-full gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="goto-team-btn">
                    Continuer <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== STEP 6: TEAM ===== */}
          {step === 6 && (
            <div data-testid="step-team">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Invitez votre équipe</h2>
              <p className="text-gray-500 text-sm mb-5">Ajoutez un membre pour collaborer. Vous pourrez en ajouter d'autres plus tard.</p>
              {!memberInvited ? (
                <>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-gray-600 font-medium">Nom complet *</Label>
                      <Input value={teamMember.name} onChange={e => setTeamMember({...teamMember, name: e.target.value})}
                        placeholder="Jean Dupont" className="mt-1 bg-gray-50" data-testid="team-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 font-medium">Courriel *</Label>
                      <Input value={teamMember.email} onChange={e => setTeamMember({...teamMember, email: e.target.value})}
                        placeholder="jean@votreentreprise.com" className="mt-1 bg-gray-50" data-testid="team-email" />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 font-medium">Role</Label>
                      <select value={teamMember.role} onChange={e => setTeamMember({...teamMember, role: e.target.value})}
                        className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="team-role">
                        <option value="user">Vendeur</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <Button variant="outline" onClick={() => setStep(5)} className="gap-1.5 text-gray-700 border-gray-300"><ArrowLeft className="w-4 h-4" /></Button>
                    <Button variant="ghost" onClick={() => setStep(7)} className="text-gray-400">Passer</Button>
                    <Button onClick={inviteTeamMember} disabled={saving} className="flex-1 gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="invite-team-btn">
                      {saving ? 'Ajout...' : 'Ajouter'} <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-base font-bold text-gray-900">{teamMember.name}</p>
                  <p className="text-sm text-gray-500 mb-5">Ajouté à votre équipe!</p>
                  <Button onClick={() => setStep(7)} className="w-full gap-2 text-white" style={{ background: '#2BAEF5' }}>
                    Terminer la configuration <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== DONE ===== */}
          {step === 7 && (
            <div className="text-center" data-testid="step-done">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Tout est pret!</h2>
              <p className="text-gray-500 text-sm mb-6">Votre concessionnaire est 100% configuré et opérationnel.</p>
              {subdomain && (
                <a href={siteUrl} target="_blank" rel="noopener noreferrer"
                  className="block bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-6 hover:bg-gray-100 transition-colors group" data-testid="site-link">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">Votre site web</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-[#2BAEF5] font-mono font-bold text-sm">{subdomain}.autohub360.ca</code>
                    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#2BAEF5] transition-colors" />
                  </div>
                </a>
              )}
              <div className="space-y-2 text-left mb-6">
                {[
                  'Identité et logo configurés',
                  'Coordonnées et horaires enregistrés',
                  'Taxes et permis configurés',
                  'Site web en ligne avec votre design',
                  vehicleAdded ? 'Premier véhicule dans l\'inventaire' : null,
                  memberInvited ? 'Membre d\'équipe invité' : null,
                  'Essai gratuit de 14 jours active',
                ].filter(Boolean).map((item) => (
                  <div key={item} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
              <Button onClick={finish} className="w-full py-5 text-base gap-2 text-white" style={{ background: '#2BAEF5' }} data-testid="finish-onboarding-btn">
                Acceder a mon CRM <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
